import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Endpoint } from '@enums/endpoint.enum';
import { AuthService } from '@services/auth.service';
import { firstValueFrom, from, Observable, switchMap, take, throwError } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private refreshPromise: Promise<boolean> | null = null;

  constructor(
    private router: Router,
    private auth: AuthService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    const token = this.auth.getAccessToken();

    if (!token?.jwt) {
      return next.handle(req);
    }

    if (token.isExpired()) {
      this.auth.removeAccessToken();
      this.router.navigate(['/auth/login']);

      return throwError(() => new Error('Token expired'));
    }

    if (token.isExpireSoon()) {

      if (this.refreshPromise !== null) {
        return from(this.refreshPromise).pipe(
          switchMap((success) => {
            if (!success) {
              return throwError(() => new Error('Token refresh failed'));
            }

            const newToken = this.auth.getAccessToken();
            if (!newToken?.jwt) {
              return throwError(() => new Error('Token missing after refresh'));
            }

            const authReq = this.addAuthHeader(req);

            return next.handle(authReq);
          })
        );
      }

      this.refreshPromise = firstValueFrom(this.auth.refreshToken())
        .then((success: boolean) => {
          return success;
        })
        .catch((err) => {
          return false;
        })
        .finally(() => {
          this.refreshPromise = null;
          this.auth.removeAccessToken();
          this.router.navigate(['/auth/login']);
        });

      return from(this.refreshPromise).pipe(
        switchMap((success) => {
          if (!success) {
            return throwError(() => new Error('Token refresh failed'));
          }

          const newToken = this.auth.getAccessToken();
          if (!newToken || !newToken.jwt) {
            return throwError(() => new Error('Token missing after refresh'));
          }

          const authReq = this.addAuthHeader(req);

          return next.handle(authReq);
        })
      );
    }

    const authReq = this.addAuthHeader(req);

    return next.handle(authReq);
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {

    const token = this.auth.getAccessToken();

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token.jwt}`,
      },
    });
  }

  private shouldSkip(req: HttpRequest<any>): boolean {
    const skipRoutes = [Endpoint.TOKEN, Endpoint.REFRESH_TOKEN];

    return skipRoutes.some(path => req.url.includes(path));
  }
}